<template>
  <base-section id="calculation-bank" space="5">
    <v-container fluid class="calculator-bank-shell">
      <v-card outlined class="bank-page pa-3 pa-md-4">
        <div class="compact-header">
          <div>
            <div class="compact-title">Kalkulator Kontribusi Bank</div>
          </div>
          <div class="compact-badges">
            <div class="mini-badge">
              <span>Rate</span>
              <strong>{{ formatPercent(ratePercent) }}</strong>
            </div>
            <div class="mini-badge">
              <span>Tenor</span>
              <strong>{{ safeTenorMonths }} Bln</strong>
            </div>
            <div
              class="mini-badge mini-badge--status"
              :class="{ 'mini-badge--ready': isSimulationReady }"
            >
              <span>Status</span>
              <strong>{{ isSimulationReady ? "Ready" : "Draft" }}</strong>
            </div>
          </div>
        </div>

        <v-tabs
          v-model="tab"
          grow
          background-color="transparent"
          color="#103d70"
          slider-color="#155852"
          class="document-tabs__nav document-tabs__nav--top"
        >
          <v-tab href="#tab-1">Simulasi Baru</v-tab>
          <v-tab v-if="!isCimbUserGroup" href="#tab-2"
            >Pengajuan Polis dari Cover Note</v-tab
          >
        </v-tabs>

        <v-row v-if="tab === 'tab-1'" class="compact-layout">
          <v-col cols="12" lg="8" class="pr-lg-2">
            <v-card outlined class="bank-panel bank-panel--flat pa-3">
              <div class="panel-heading">
                <div>
                  <div class="panel-kicker">Input</div>
                  <div class="panel-title">Data Simulasi</div>
                </div>
              </div>

              <v-row dense>
                <v-col cols="12">
                  <div class="field-group-title">Informasi Polis</div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="form.bankName"
                    label="Bank"
                    outlined
                    dense
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="form.branchName"
                    label="Cabang *"
                    outlined
                    dense
                    :error-messages="branchError"
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="form.contractDate"
                    label="Tanggal Kontrak"
                    type="date"
                    outlined
                    dense
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>

                <v-col cols="12">
                  <div class="field-group-title">Periode Proteksi</div>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.participantName"
                    label="Peserta / Debitur"
                    outlined
                    dense
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.phone"
                    label="No HP Peserta"
                    outlined
                    dense
                    :error-messages="phoneError"
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.email"
                    label="Email Peserta"
                    outlined
                    dense
                    :error-messages="emailError"
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="form.periodStartDate"
                    label="Start Date"
                    type="date"
                    outlined
                    dense
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="form.periodEndDate"
                    label="End Date"
                    type="date"
                    outlined
                    dense
                    :error-messages="periodError"
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    :value="policyPeriodLabel"
                    label="Periode Excel"
                    outlined
                    dense
                    readonly
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>

                <v-col cols="12">
                  <div class="field-group-title">Risiko dan Okupasi</div>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.riskLocation"
                    label="Lokasi Resiko"
                    outlined
                    dense
                    rows="2"
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="3" class="field-row-spaced">
                  <v-text-field
                    v-model="form.zipCode"
                    label="Kode Pos"
                    outlined
                    dense
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>

                <v-col cols="12" sm="6" md="3" class="field-row-spaced">
                  <v-select
                    v-model="form.escalationClause"
                    :items="escalationOptions"
                    item-text="label"
                    item-value="id"
                    label="Escalation Clause"
                    outlined
                    dense
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="3" class="field-row-spaced">
                  <v-select
                    v-model="form.occupancyId"
                    :items="occupancyOptions"
                    item-text="label"
                    item-value="id"
                    label="Okupasi"
                    outlined
                    dense
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="3" class="field-row-spaced">
                  <v-text-field
                    :value="selectedOccupancy ? selectedOccupancy.code : '-'"
                    label="Kode Okupasi"
                    outlined
                    dense
                    readonly
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>

                <v-col cols="12">
                  <div class="field-group-title">Nilai dan Biaya</div>
                  <div class="field-group-caption">
                    Gunakan nominal penuh, mis. `100000000` atau `100.000.000`.
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="3">
                  <v-text-field
                    :value="formatCurrency(form.buildingValue)"
                    label="Nilai Bangunan"
                    outlined
                    dense
                    hide-details="auto"
                    class="bank-input"
                    @input="updateCurrency('buildingValue', $event)"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="3">
                  <v-text-field
                    :value="formatCurrency(form.furnitureValue)"
                    label="Nilai Perabot"
                    outlined
                    dense
                    hide-details="auto"
                    class="bank-input"
                    @input="updateCurrency('furnitureValue', $event)"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="3">
                  <v-text-field
                    :value="formatCurrency(form.equipmentValue)"
                    label="Nilai Mesin/Peralatan"
                    outlined
                    dense
                    hide-details="auto"
                    class="bank-input"
                    @input="updateCurrency('equipmentValue', $event)"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="3">
                  <v-text-field
                    :value="formatCurrency(form.stockValue)"
                    label="Nilai Stok"
                    outlined
                    dense
                    hide-details="auto"
                    class="bank-input"
                    @input="updateCurrency('stockValue', $event)"
                  />
                </v-col>

                <v-col cols="12" sm="6" md="4" lg="3" class="field-row-spaced">
                  <v-text-field
                    :value="formatCurrency(sumInsured)"
                    label="Total Sum Insured Terbaca"
                    outlined
                    dense
                    readonly
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="3" class="field-row-spaced">
                  <v-text-field
                    :value="safeTenorMonths"
                    label="Tenor Bulan"
                    outlined
                    dense
                    readonly
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="3" class="field-row-spaced">
                  <v-text-field
                    :value="policyYears"
                    label="Periode Tahun"
                    outlined
                    dense
                    readonly
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="3" class="field-row-spaced">
                  <v-text-field
                    :value="remainingDays"
                    label="Sisa Hari"
                    outlined
                    dense
                    readonly
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>

                <v-col cols="12" md="6" class="field-row-spaced">
                  <v-text-field
                    :value="formatCurrency(policyFeeAmount)"
                    label="Biaya Admin + Materai"
                    outlined
                    dense
                    readonly
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
                <v-col cols="12" md="6" class="field-row-spaced">
                  <v-text-field
                    :value="formatCurrency(escalatedSumInsured)"
                    label="TSI Setelah Escalation Terbaca"
                    outlined
                    dense
                    readonly
                    hide-details="auto"
                    class="bank-input"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12" lg="4" class="pl-lg-2">
            <v-card outlined class="summary-card summary-card--compact pa-3">
              <div class="summary-card__title">Ringkasan Hasil</div>

              <div class="gross-box">
                <div class="gross-box__label">Gross</div>
                <div class="gross-box__value">
                  Rp {{ formatCurrency(grossContribution) }}
                </div>
                <div class="gross-box__meta">Total gross kontribusi</div>
              </div>

              <div class="compact-breakdown">
                <div class="compact-breakdown__row">
                  <span>Kontribusi ({{ formatPercent(ratePercent) }})</span>
                  <strong>Rp {{ formatCurrency(baseContribution) }}</strong>
                </div>
                <div class="compact-breakdown__row">
                  <span>Biaya Admin + Materai</span>
                  <strong>Rp {{ formatCurrency(policyFeeAmount) }}</strong>
                </div>
                <div class="compact-breakdown__row">
                  <span>Ujrah Bank (15%)</span>
                  <strong>Rp {{ formatCurrency(bankUjrahAmount) }}</strong>
                </div>
                <div class="compact-breakdown__row">
                  <span>PPN (11%)</span>
                  <strong>Rp {{ formatCurrency(ppnAmount) }}</strong>
                </div>
                <div class="compact-breakdown__row compact-breakdown__row--net">
                  <span>Total Net Kontribusi</span>
                  <strong>Rp {{ formatCurrency(netContribution) }}</strong>
                </div>
              </div>

              <div class="compact-info-grid">
                <div class="compact-info">
                  <span>Okupasi</span>
                  <strong>{{
                    selectedOccupancy ? selectedOccupancy.code : "-"
                  }}</strong>
                </div>
                <div class="compact-info">
                  <span>Escalation</span>
                  <strong>{{ selectedEscalationDisplay }}</strong>
                </div>
                <div class="compact-info">
                  <span>Periode</span>
                  <strong>{{ policyPeriodLabel }}</strong>
                </div>
                <div class="compact-info">
                  <span>TSI Escalation</span>
                  <strong>Rp {{ formatCurrency(escalatedSumInsured) }}</strong>
                </div>
              </div>

              <div class="summary-note mt-2">
                TSI dasar terbaca: Rp {{ formatCurrency(sumInsured) }}
              </div>

              <div
                class="summary-note"
                :class="{ 'summary-note--error': !isSimulationReady }"
              >
                {{ readinessMessage }}
              </div>

              <div class="document-tab-panel mt-2">
                <div class="action-buttons action-buttons--compact">
                  <v-btn
                    v-if="getFastFlowLoginUser() !== 'brocade'"
                    color="secondary"
                    depressed
                    class="action-btn action-btn--secondary"
                    :disabled="!isSimulationReady"
                    :loading="submittingDocumentType === 'COVERNOTE'"
                    @click="ProcessFastFlowDocument('COVERNOTE')"
                  >
                    <v-icon left small>mdi-file-sign</v-icon>
                    COVER NOTE
                  </v-btn>
                  <v-btn
                    v-if="getFastFlowLoginUser() === 'brocade'"
                    color="primary"
                    depressed
                    class="action-btn action-btn--primary"
                    :disabled="!isSimulationReady"
                    :loading="submittingDocumentType === 'EPOLIS'"
                    @click="ProcessFastFlowDocument('EPOLIS')"
                  >
                    <v-icon left small>mdi-file-document</v-icon>
                    E - Polis
                  </v-btn>
                  <v-btn
                    v-if="!isCimbUserGroup"
                    color="primary"
                    depressed
                    class="action-btn action-btn--primary"
                    :disabled="!isSimulationReady"
                    :loading="submittingDocumentType === 'EPOLIS'"
                    @click="CreatePolicy"
                  >
                    <v-icon left small>mdi-shield-check</v-icon>
                    CREATE POLICY
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else-if="!isCimbUserGroup" class="compact-layout">
          <v-col cols="12">
            <v-card outlined class="bank-panel bank-panel--flat pa-4">
              <div class="policy-conversion-panel">
                <div class="policy-conversion-panel__head">
                  <div class="panel-kicker">Quick Action</div>
                  <div class="panel-title">
                    Pengajuan Polis dari Cover Note
                  </div>
                  <div class="policy-conversion-panel__caption">
                    Masukkan kode TAC pada cover note untuk generate polis.
                  </div>
                </div>

                <v-row dense>
                  <v-col cols="12" md="8">
                    <v-text-field
                      v-model="coverNoteRequest.fastflowId"
                      label="Masukkan kode TAC Cover Note"
                      outlined
                      dense
                      hide-details="auto"
                      class="bank-input"
                      @input="handleCoverNoteInput"
                      @keyup.enter="LookupCoverNote"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="policy-conversion-actions">
                      <v-btn
                        color="secondary"
                        depressed
                        class="action-btn action-btn--secondary action-btn--slim"
                        :loading="coverNoteRequest.loading"
                        :disabled="coverNoteRequest.submitting"
                        @click="LookupCoverNote"
                      >
                        <v-icon left small>mdi-magnify</v-icon>
                        CARI
                      </v-btn>
                      <v-btn
                        color="primary"
                        depressed
                        class="action-btn action-btn--primary action-btn--slim"
                        :loading="coverNoteRequest.submitting"
                        :disabled="coverNoteRequest.loading"
                        @click="openPolicySubmissionDialog"
                      >
                        <v-icon left small>mdi-file-document-check</v-icon>
                        AJUKAN POLICY
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <div
                  v-if="coverNoteRequest.record"
                  class="policy-conversion-result"
                >
                  <div class="policy-conversion-result__item">
                    <span>No Register</span>
                    <strong>{{
                      coverNoteRequest.record.fastflow_id || "-"
                    }}</strong>
                  </div>
                  <div class="policy-conversion-result__item">
                    <span>Status</span>
                    <strong>{{
                      coverNoteRequest.record.fastflow_statusdesc ||
                        coverNoteRequest.record.fastflow_status ||
                        "-"
                    }}</strong>
                  </div>
                  <div class="policy-conversion-result__item">
                    <span>Peserta</span>
                    <strong>{{
                      coverNoteRequest.record.fastflow_insured || "-"
                    }}</strong>
                  </div>
                  <div class="policy-conversion-result__item">
                    <span>Email</span>
                    <strong>{{
                      coverNoteRequest.record.fastflow_email || "-"
                    }}</strong>
                  </div>
                  <div class="policy-conversion-result__item">
                    <span>Cabang</span>
                    <strong>{{
                      coverNoteRequest.record.fastflow_fieldvaluedesc_6 || "-"
                    }}</strong>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

    <v-dialog v-model="policySubmissionDialog.open" max-width="560" persistent>
      <v-card class="pa-2">
        <v-card-title class="panel-title">Pilih Marketing Officer</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="policySubmissionDialog.marketingOfficerId"
            :search-input.sync="policySubmissionDialog.marketingOfficerSearch"
            :items="policySubmissionDialog.marketingOfficers"
            :loading="policySubmissionDialog.loadingMarketingOfficers"
            item-text="mo_label"
            item-value="users_id"
            label="Marketing Officer"
            outlined
            dense
            clearable
            no-filter
            class="bank-input"
            @keydown="handleMarketingOfficerKeydown"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.mo_label }}</v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="coverNoteRequest.submitting"
            @click="closePolicySubmissionDialog"
            >Batal</v-btn
          >
          <v-btn
            color="primary"
            depressed
            :loading="coverNoteRequest.submitting"
            :disabled="!policySubmissionDialog.marketingOfficerId"
            @click="confirmPolicySubmission"
          >
            Ajukan Policy
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      v-model="snackbar.dialog"
      rounded="pill"
      bottom
    >
      {{ snackbar.text }}
      <template>
        <v-btn text @click.native="snackbar.dialog = false">Close</v-btn>
      </template>
    </v-snackbar>
  </base-section>
</template>

<script>
const OCCUPANCY_OPTIONS = [
  { id: "2976-home", code: "2976", label: "Rumah Tinggal", baseRate: 0.0294 },
  {
    id: "2934-ruko",
    code: "2934",
    label: "Rumah Toko (RUKO)",
    baseRate: 0.152
  },
  {
    id: "2971-rukan",
    code: "2971",
    label: "Rumah Kantor (RUKAN)",
    baseRate: 0.0368
  },
  {
    id: "2971-apartment-6",
    code: "2971",
    label: "Apartmen <=6 lantai termasuk basemen",
    baseRate: 0.0368
  },
  {
    id: "2972-apartment-18",
    code: "2972",
    label: "Apartmen 7-18 lantai termasuk basemen",
    baseRate: 0.0385
  },
  {
    id: "2973-apartment-24",
    code: "2973",
    label: "Apartmen 18-24 lantai termasuk basemen",
    baseRate: 0.0376
  },
  {
    id: "2975-apartment-gt24",
    code: "2975",
    label: "Apartmen >24 lantai termasuk basemen",
    baseRate: 0.0455
  },
  { id: "29761-kost", code: "29761", label: "Rumah Kost", baseRate: 0.0478 }
];

const ESCALATION_NONE_ID = "TANPA ESCALATION CLAUSE";
const ESCALATION_3_ID = "ESCALATION CLAUSE 3%";
const ESCALATION_5_ID = "ESCALATION CLAUSE 5%";

const ESCALATION_OPTIONS = [
  {
    id: ESCALATION_NONE_ID,
    label: "Tanpa Escalation Clause",
    rate: 0
  },
  {
    id: ESCALATION_3_ID,
    label: "Escalation Clause 3%",
    rate: 0.03
  },
  {
    id: ESCALATION_5_ID,
    label: "Escalation Clause 5%",
    rate: 0.05
  }
];

const ADMIN_FEE = 30000;
const DEFAULT_CLASS = "I";
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const TODAY = new Date().toISOString().slice(0, 10);
const DEFAULT_END_DATE = (() => {
  const endDate = new Date();
  endDate.setFullYear(endDate.getFullYear() + 1);
  return endDate.toISOString().slice(0, 10);
})();

export default {
  name: "CalculatorBank",
  data: () => ({
    tab: "tab-1",
    userGroup: "",
    escalationOptions: ESCALATION_OPTIONS,
    form: {
      bankName: "",
      branchName: "",
      participantName: "",
      phone: "",
      email: "",
      riskLocation: "",
      contractDate: TODAY,
      periodStartDate: TODAY,
      periodEndDate: DEFAULT_END_DATE,
      zipCode: "",
      buildingValue: 0,
      furnitureValue: 0,
      equipmentValue: 0,
      stockValue: 0,
      constructionClass: DEFAULT_CLASS,
      occupancyId: "2976-home",
      escalationClause: ESCALATION_3_ID,
      policyFee: ADMIN_FEE
    },
    coverNoteRequest: {
      fastflowId: "",
      loading: false,
      submitting: false,
      record: null
    },
    policySubmissionDialog: {
      open: false,
      branchId: "",
      marketingOfficerId: "",
      marketingOfficerSearch: "",
      branches: [],
      marketingOfficers: [],
      loadingBranches: false,
      loadingMarketingOfficers: false,
      marketingOfficerSearchTimer: null,
      marketingOfficerRequestId: 0
    },
    submittingDocumentType: "",
    snackbar: {
      dialog: false,
      color: "success",
      text: "",
      timeout: 3000
    }
  }),
  created() {
    this.form.bankName = this.getStoredBankName();
    this.userGroup = this.getStoredUserGroup();
  },
  computed: {
    occupancyOptions() {
      return OCCUPANCY_OPTIONS;
    },
    isCimbUserGroup() {
      return this.userGroup === "cimb";
    },
    selectedOccupancy() {
      return (
        this.occupancyOptions.find(item => item.id === this.form.occupancyId) ||
        null
      );
    },
    selectedEscalation() {
      return this.resolveEscalationOption(this.form.escalationClause);
    },
    periodError() {
      const startDate = this.parseDateValue(this.form.periodStartDate);
      const endDate = this.parseDateValue(this.form.periodEndDate);

      if (!startDate || !endDate) {
        return "";
      }

      return endDate <= startDate
        ? "Periode akhir harus lebih besar dari tanggal awal"
        : "";
    },
    policyYears() {
      return this.calculatePolicyYears(
        this.form.periodStartDate,
        this.form.periodEndDate
      );
    },
    remainingDays() {
      return this.calculateRemainingDays(
        this.form.periodStartDate,
        this.form.periodEndDate,
        this.policyYears
      );
    },
    policyYearDays() {
      return this.calculatePolicyYearDays(
        this.form.periodStartDate,
        this.policyYears
      );
    },
    policyRatePeriods() {
      if (this.policyTotalDays <= 0) {
        return 0;
      }

      return this.policyYears + (this.remainingDays > 0 ? 1 : 0);
    },
    policyTotalDays() {
      return this.calculateDateDiffDays(
        this.form.periodStartDate,
        this.form.periodEndDate
      );
    },
    policyPeriodLabel() {
      if (
        !this.form.periodStartDate ||
        !this.form.periodEndDate ||
        this.periodError
      ) {
        return "-";
      }

      return `${this.policyYears} Tahun ${this.remainingDays} Hari`;
    },
    safeTenorMonths() {
      return this.calculateTenorMonths(
        this.form.periodStartDate,
        this.form.periodEndDate
      );
    },
    sumInsured() {
      return (
        this.normalizeNumber(this.form.buildingValue) +
        this.normalizeNumber(this.form.furnitureValue) +
        this.normalizeNumber(this.form.equipmentValue) +
        this.normalizeNumber(this.form.stockValue)
      );
    },
    tsiError() {
      if (this.sumInsured > 0) {
        return "";
      }

      return "Isi Field Terlebih Dahulu";
    },
    ratePercent() {
      if (!this.selectedOccupancy) {
        return 0;
      }

      return this.calculateFinalRatePercent(
        this.selectedOccupancy.baseRate,
        this.policyYears,
        this.remainingDays,
        this.selectedEscalation.id
      );
    },
    fullYearRatePercent() {
      if (!this.selectedOccupancy) {
        return 0;
      }

      return this.calculateCumulativeRatePercent(
        this.selectedOccupancy.baseRate,
        this.policyYears,
        this.selectedEscalation.id
      );
    },
    selectedEscalationDisplay() {
      return this.selectedEscalation ? this.selectedEscalation.label : "-";
    },
    escalatedSumInsured() {
      return this.calculateEscalatedSumInsured(
        this.sumInsured,
        this.selectedEscalation.id,
        this.policyRatePeriods
      );
    },
    phoneError() {
      return "";
    },
    emailError() {
      if (this.form.email.trim() === "") {
        return "";
      }

      return /.+@.+\..+/.test(this.form.email.trim())
        ? ""
        : "Email peserta harus valid";
    },
    branchError() {
      return this.form.branchName.trim() === "" ? "Cabang wajib diisi" : "";
    },
    isSimulationReady() {
      return (
        !this.branchError &&
        this.form.participantName.trim() !== "" &&
        this.form.riskLocation.trim() !== "" &&
        this.form.zipCode.trim() !== "" &&
        !this.periodError &&
        !this.tsiError &&
        !this.phoneError &&
        !this.emailError &&
        this.policyTotalDays > 0 &&
        this.sumInsured > 0 &&
        !!this.selectedOccupancy
      );
    },
    fullYearContribution() {
      return this.roundUpValue(
        this.sumInsured * (this.fullYearRatePercent / 100)
      );
    },
    dailyContribution() {
      if (this.remainingDays <= 0 || !this.selectedOccupancy) {
        return 0;
      }

      return this.roundUpValue(
        (this.sumInsured *
          (this.selectedOccupancy.baseRate / 100) *
          this.remainingDays) /
          365
      );
    },
    baseContribution() {
      return this.fullYearContribution + this.dailyContribution;
    },
    policyFeeAmount() {
      return this.roundValue(this.form.policyFee);
    },
    bankUjrahAmount() {
      return this.roundUpValue(this.baseContribution * 0.15);
    },
    ppnAmount() {
      return this.roundUpValue(this.bankUjrahAmount * 0.11);
    },
    netContribution() {
      return this.grossContribution - this.bankUjrahAmount - this.ppnAmount;
    },
    grossContribution() {
      return this.baseContribution + this.policyFeeAmount;
    },
    ujrahAmount() {
      return this.baseContribution * 0.425;
    },
    tabarruAmount() {
      return this.baseContribution * 0.575;
    },
    readinessMessage() {
      if (this.periodError) {
        return this.periodError;
      }

      if (this.tsiError) {
        return this.tsiError;
      }

      return this.isSimulationReady
        ? "Data siap dipakai untuk preview dokumen dan create policy."
        : "Lengkapi data utama agar kalkulasi Excel aktif.";
    }
  },
  methods: {
    getStoredBankName() {
      return String(localStorage.getItem("website_users_name") || "").trim();
    },
    getStoredUserAddress() {
      return String(localStorage.getItem("website_users_address") || "").trim();
    },
    getStoredUserGroup() {
      return String(localStorage.getItem("website_users_group") || "")
        .trim()
        .toLowerCase();
    },
    normalizeNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },
    parseDateValue(value) {
      if (!value) {
        return null;
      }

      const date = new Date(`${value}T00:00:00`);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    calculateDateDiffDays(startValue, endValue) {
      const startDate = this.parseDateValue(startValue);
      const endDate = this.parseDateValue(endValue);

      if (!startDate || !endDate || endDate <= startDate) {
        return 0;
      }

      return Math.ceil((endDate.getTime() - startDate.getTime()) / DAY_IN_MS);
    },
    calculatePolicyYears(startValue, endValue) {
      const startDate = this.parseDateValue(startValue);
      const endDate = this.parseDateValue(endValue);

      if (!startDate || !endDate || endDate <= startDate) {
        return 0;
      }

      let years = endDate.getFullYear() - startDate.getFullYear();
      const anniversary = new Date(startDate);
      anniversary.setFullYear(startDate.getFullYear() + years);

      if (anniversary > endDate) {
        years -= 1;
      }

      return years < 0 ? 0 : years;
    },
    calculateRemainingDays(startValue, endValue, years) {
      const startDate = this.parseDateValue(startValue);
      const endDate = this.parseDateValue(endValue);

      if (!startDate || !endDate || endDate <= startDate) {
        return 0;
      }

      const anniversary = new Date(startDate);
      anniversary.setFullYear(startDate.getFullYear() + years);

      if (anniversary > endDate) {
        return 0;
      }

      return Math.ceil((endDate.getTime() - anniversary.getTime()) / DAY_IN_MS);
    },
    calculateTenorMonths(startValue, endValue) {
      const startDate = this.parseDateValue(startValue);
      const endDate = this.parseDateValue(endValue);

      if (!startDate || !endDate || endDate <= startDate) {
        return 0;
      }

      return (
        (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        (endDate.getMonth() - startDate.getMonth())
      );
    },
    calculatePolicyYearDays(startValue, years) {
      const startDate = this.parseDateValue(startValue);

      if (!startDate) {
        return 365;
      }

      const anniversary = new Date(startDate);
      anniversary.setFullYear(startDate.getFullYear() + years);

      const nextAnniversary = new Date(anniversary);
      nextAnniversary.setFullYear(anniversary.getFullYear() + 1);

      const diffDays = Math.ceil(
        (nextAnniversary.getTime() - anniversary.getTime()) / DAY_IN_MS
      );

      return diffDays > 0 ? diffDays : 365;
    },
    calculateCumulativeRatePercent(baseRate, fullYears, clauseValue) {
      const escalationRate = this.getEscalationRate(clauseValue);

      if (fullYears <= 0) {
        return 0;
      }

      if (escalationRate <= 0) {
        return baseRate * fullYears;
      }

      return (
        baseRate *
        ((Math.pow(1 + escalationRate, fullYears) - 1) / escalationRate)
      );
    },
    calculateFinalRatePercent(baseRate, fullYears, remainingDays, clauseValue) {
      if (baseRate <= 0 || fullYears < 0 || remainingDays < 0) {
        return 0;
      }

      const escalationRate = this.getEscalationRate(clauseValue);

      if (fullYears <= 0 && remainingDays <= 0) {
        return 0;
      }

      if (escalationRate <= 0) {
        return baseRate;
      }

      return (
        this.calculateCumulativeRatePercent(baseRate, fullYears, clauseValue) +
        (remainingDays > 0 ? baseRate : 0)
      );
    },
    calculateEscalatedSumInsured(sumInsured, clauseValue, ratePeriods) {
      const normalizedClause = this.normalizeEscalationClause(clauseValue);
      const normalizedSumInsured = this.normalizeNumber(sumInsured);

      if (normalizedSumInsured <= 0) {
        return 0;
      }

      if (normalizedClause === "TANPA ESCALATION CLAUSE") {
        return normalizedSumInsured;
      }

      const escalationRate = this.getEscalationRate(normalizedClause);
      const exponent = Math.max(this.normalizeNumber(ratePeriods) - 1, 0);

      return normalizedSumInsured * Math.pow(1 + escalationRate, exponent);
    },
    roundUpValue(value) {
      return Math.ceil(this.normalizeNumber(value));
    },
    roundValue(value) {
      return Math.round(this.normalizeNumber(value));
    },
    parseCurrency(value) {
      if (typeof value === "number") {
        return value;
      }

      const numeric = String(value || "").replace(/[^\d]/g, "");
      return numeric ? Number(numeric) : 0;
    },
    updateCurrency(field, value) {
      this.form[field] = this.parseCurrency(value);
    },
    normalizeEscalationClause(value) {
      return String(value || "")
        .trim()
        .toUpperCase();
    },
    resolveEscalationOption(value) {
      const normalizedValue = this.normalizeEscalationClause(value);
      const exactOption = this.escalationOptions.find(
        item => item.id === normalizedValue
      );

      if (exactOption) {
        return exactOption;
      }

      // Excel compares the clause text directly; normalize incoming labels so the branch stays aligned.
      if (normalizedValue.includes("3%")) {
        return this.escalationOptions.find(item => item.id === ESCALATION_3_ID);
      }

      if (normalizedValue.includes("5%")) {
        return this.escalationOptions.find(item => item.id === ESCALATION_5_ID);
      }

      if (normalizedValue.includes("TANPA")) {
        return this.escalationOptions.find(
          item => item.id === ESCALATION_NONE_ID
        );
      }

      return this.escalationOptions[0];
    },
    getEscalationRate(clauseValue) {
      const clause = this.resolveEscalationOption(clauseValue);
      return clause ? clause.rate : 0;
    },
    getFastFlowLoginUser() {
      return (this.$functions.UsersID && this.$functions.UsersID()) || "";
    },
    normalizeFastFlowId(value) {
      return `${value || ""}`.trim().toUpperCase();
    },
    handleCoverNoteInput() {
      if (
        this.coverNoteRequest.record &&
        this.normalizeFastFlowId(this.coverNoteRequest.fastflowId) !==
          this.normalizeFastFlowId(this.coverNoteRequest.record.fastflow_id)
      ) {
        this.coverNoteRequest.record = null;
      }
    },
    resolveFastFlowLookupRecord(rows, fastflowId) {
      const records = Array.isArray(rows) ? rows.filter(Boolean) : [];
      const normalizedId = this.normalizeFastFlowId(fastflowId);
      const exactRecord = records.find(item => {
        return (
          this.normalizeFastFlowId(item.fastflow_id) === normalizedId &&
          `${item.fastflow_row || ""}`.trim() !== ""
        );
      });

      if (exactRecord) {
        return exactRecord;
      }

      return (
        records.find(item => `${item.fastflow_row || ""}`.trim() !== "") || null
      );
    },
    FetchCoverNoteRecord(fastflowId) {
      const targetId = this.normalizeFastFlowId(fastflowId);
      const formData = new FormData();
      formData.append("datavalue", targetId);
      formData.append("fastflow_id", targetId);

      return this.$axios
        .post(this.$functions.SafeURL("apiListFastFlow", ""), formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then(response => {
          const record = this.resolveFastFlowLookupRecord(
            response.data,
            targetId
          );

          if (!record || !record.fastflow_row) {
            throw new Error("Cover note tidak ditemukan");
          }

          return record;
        });
    },
    LookupCoverNote(showFeedback = true) {
      const fastflowId = this.normalizeFastFlowId(
        this.coverNoteRequest.fastflowId
      );

      if (fastflowId === "") {
        this.SnackBar(true, "error", "Fastflow ID wajib diisi", 3000);
        return Promise.resolve(null);
      }

      this.coverNoteRequest.loading = true;
      return this.FetchCoverNoteRecord(fastflowId)
        .then(record => {
          this.coverNoteRequest.fastflowId = this.normalizeFastFlowId(
            record.fastflow_id || fastflowId
          );
          this.coverNoteRequest.record = record;

          if (showFeedback) {
            this.SnackBar(
              true,
              "success",
              `Cover note ${this.coverNoteRequest.fastflowId} berhasil dimuat`,
              3500
            );
          }

          return record;
        })
        .catch(error => {
          this.coverNoteRequest.record = null;
          this.SnackBar(
            true,
            "error",
            error.message || "Gagal mengambil data cover note",
            4000
          );
          return null;
        })
        .finally(() => {
          this.coverNoteRequest.loading = false;
        });
    },
    extractFileNameFromUrl(url) {
      const targetUrl = `${url || ""}`.trim();

      if (!targetUrl) {
        return "document.pdf";
      }

      const segments = targetUrl.split("/");
      return decodeURIComponent(
        segments[segments.length - 1] || "document.pdf"
      );
    },
    triggerDocumentDownload(url) {
      const targetUrl = `${url || ""}`.trim();

      if (!targetUrl) {
        return false;
      }

      const anchor = document.createElement("a");
      anchor.href = targetUrl;
      anchor.target = "_blank";
      anchor.rel = "noopener";
      anchor.download = this.extractFileNameFromUrl(targetUrl);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      return true;
    },
    downloadFastFlowDocument(documentUrl) {
      const targetUrl = `${documentUrl || ""}`.trim();

      if (!targetUrl) {
        return Promise.resolve({
          success: false,
          feedback: "URL dokumen tidak tersedia",
          url: ""
        });
      }

      return Promise.resolve({
        success: this.triggerDocumentDownload(targetUrl),
        feedback: "",
        url: targetUrl
      });
    },
    buildFastFlowResultMessage(
      fastflowId,
      successLabel,
      failureLabel,
      downloadResult,
      backendFeedback
    ) {
      const normalizedFastFlowId = `${fastflowId || ""}`.trim();
      const hasBackendError = backendFeedback && backendFeedback !== "Y";
      const message = [
        hasBackendError
          ? `Proses ${failureLabel} gagal${
              normalizedFastFlowId ? ` ${normalizedFastFlowId}` : ""
            }.`
          : `Data berhasil diproses${
              normalizedFastFlowId ? ` ${normalizedFastFlowId}` : ""
            }.`
      ];

      if (!hasBackendError && downloadResult && downloadResult.success) {
        message.push(`Dokumen ${successLabel} berhasil diunduh.`);
      } else if (downloadResult) {
        message.push(
          `Dokumen ${failureLabel} gagal diunduh${
            downloadResult.feedback ? `: ${downloadResult.feedback}` : ""
          }.`
        );
      }

      if (backendFeedback && backendFeedback !== "Y") {
        message.push(`Proses backend: ${backendFeedback}.`);
      }

      return message.join(" ");
    },
    ValidateDocumentAction() {
      if (!this.form.branchName.trim()) {
        this.SnackBar(true, "error", "Cabang wajib diisi", 3000);
        return false;
      }

      if (!this.form.participantName.trim()) {
        this.SnackBar(
          true,
          "error",
          "Nama peserta / debitur wajib diisi",
          3000
        );
        return false;
      }

      if (!this.form.riskLocation.trim()) {
        this.SnackBar(true, "error", "Lokasi resiko wajib diisi", 3000);
        return false;
      }

      if (
        this.form.email.trim() !== "" &&
        !/.+@.+\..+/.test(this.form.email.trim())
      ) {
        this.SnackBar(true, "error", "Email peserta harus valid", 3000);
        return false;
      }

      if (!this.form.zipCode.trim()) {
        this.SnackBar(true, "error", "Kode pos wajib diisi", 3000);
        return false;
      }

      if (!this.form.periodStartDate || !this.form.periodEndDate) {
        this.SnackBar(true, "error", "Periode polis wajib diisi", 3000);
        return false;
      }

      if (this.periodError) {
        this.SnackBar(true, "error", this.periodError, 3000);
        return false;
      }

      if (this.sumInsured <= 0) {
        this.SnackBar(true, "error", this.tsiError, 3000);
        return false;
      }

      if (this.policyTotalDays <= 0) {
        this.SnackBar(
          true,
          "error",
          "Periode pertanggungan harus lebih dari 0 hari",
          3000
        );
        return false;
      }

      if (!this.selectedOccupancy) {
        this.SnackBar(true, "error", "Okupasi belum dipilih", 3000);
        return false;
      }

      return true;
    },
    CreatePolicy() {
      this.ProcessFastFlowDocument("EPOLIS");
    },
    openPolicySubmissionDialog() {
      if (this.coverNoteRequest.loading || this.coverNoteRequest.submitting)
        return;
      if (this.normalizeFastFlowId(this.coverNoteRequest.fastflowId) === "") {
        this.SnackBar(true, "error", "Fastflow ID wajib diisi", 3000);
        return;
      }
      this.policySubmissionDialog.open = true;
    },
    closePolicySubmissionDialog() {
      this.policySubmissionDialog.open = false;
      clearTimeout(this.policySubmissionDialog.marketingOfficerSearchTimer);
      this.policySubmissionDialog.marketingOfficerSearch = "";
      this.policySubmissionDialog.marketingOfficers = [];
    },
    loadPolicyBranches() {
      this.policySubmissionDialog.loadingBranches = true;
      const formData = {
        order_by: "branch_name",
        order_type: "ASC",
        limit: "500"
      };
      const query = "order_by=branch_name&order_type=ASC&limit=500";
      const param = this.$functions.ParamPOST(formData);
      this.$axios
        .post(this.$functions.SafeURL("apiListBranch", query), param, {
          headers: { "Content-Type": "text/plain; charset=ISO-8859-1" }
        })
        .then(response => {
          const items = Array.isArray(response.data) ? response.data : [];
          this.policySubmissionDialog.branches = items
            .filter(item => item.feedback === "Y" && item.branch_id)
            .map(item => ({
              ...item,
              branch_label: `${item.branch_id} - ${item.branch_name}`
            }));
          if (!this.policySubmissionDialog.branches.length) {
            const message =
              items.length && items[0].feedback !== "Y"
                ? items[0].feedback
                : "Data branch tidak ditemukan";
            this.SnackBar(true, "warning", message, 5000);
          }
        })
        .catch(error =>
          this.SnackBar(
            true,
            "error",
            error.message || "Gagal memuat branch",
            4000
          )
        )
        .finally(() => {
          this.policySubmissionDialog.loadingBranches = false;
        });
    },
    handleMarketingOfficerKeydown(event) {
      if (
        ["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab"].includes(event.key)
      ) {
        return;
      }

      clearTimeout(this.policySubmissionDialog.marketingOfficerSearchTimer);
      this.policySubmissionDialog.marketingOfficerSearchTimer = setTimeout(
        () => {
          const keyword = String(event.target.value || "").trim();
          this.policySubmissionDialog.marketingOfficerSearch = keyword;

          if (!keyword) {
            this.policySubmissionDialog.marketingOfficers = [];
            return;
          }

          this.loadMarketingOfficers(keyword);
        },
        350
      );
    },
    loadMarketingOfficers(keyword) {
      this.policySubmissionDialog.marketingOfficers = [];
      const searchKeyword = String(keyword || "").trim();
      if (!searchKeyword) return;

      const requestId = ++this.policySubmissionDialog.marketingOfficerRequestId;
      this.policySubmissionDialog.loadingMarketingOfficers = true;
      const formData = {
        users_actived: "Y",
        users_name: searchKeyword,
        order_by: "a.users_name",
        order_type: "ASC",
        limit: "50"
      };
      const query =
        `users_actived=Y&users_name=${encodeURIComponent(searchKeyword)}` +
        "&order_by=a.users_name&order_type=ASC&limit=50";
      const param = this.$functions.ParamPOST(formData);
      this.$axios
        .post(this.$functions.SafeURL("apiListUsers", query), param, {
          headers: { "Content-Type": "text/plain; charset=ISO-8859-1" }
        })
        .then(response => {
          if (
            requestId !== this.policySubmissionDialog.marketingOfficerRequestId
          )
            return;

          const items = Array.isArray(response.data) ? response.data : [];
          this.policySubmissionDialog.marketingOfficers = items.length
            ? items
                .filter(
                  item =>
                    item.feedback === "Y" &&
                    ["y", "1", "true"].includes(
                      String(item.users_marketingf || "").toLowerCase()
                    )
                )
                .map(item => ({
                  ...item,
                  mo_label: `${item.users_id} - ${item.users_name}`
                }))
                .filter(item => item.users_id)
            : [];
          if (!this.policySubmissionDialog.marketingOfficers.length) {
            const message =
              items.length && items[0].feedback !== "Y"
                ? items[0].feedback
                : "Marketing Officer tidak ditemukan";
            this.SnackBar(true, "warning", message, 5000);
          }
        })
        .catch(error => {
          if (
            requestId !== this.policySubmissionDialog.marketingOfficerRequestId
          )
            return;

          this.SnackBar(
            true,
            "error",
            error.message || "Gagal memuat marketing officer",
            4000
          );
        })
        .finally(() => {
          if (
            requestId === this.policySubmissionDialog.marketingOfficerRequestId
          )
            this.policySubmissionDialog.loadingMarketingOfficers = false;
        });
    },
    confirmPolicySubmission() {
      if (!this.policySubmissionDialog.marketingOfficerId) {
        this.SnackBar(true, "error", "Marketing Officer wajib dipilih", 3000);
        return;
      }
      this.SubmitPolicyFromCoverNote(true);
    },
    SubmitPolicyFromCoverNote(confirmed = false) {
      if (confirmed !== true) {
        this.openPolicySubmissionDialog();
        return;
      }
      if (this.coverNoteRequest.loading || this.coverNoteRequest.submitting) {
        return;
      }

      const loginUser = this.getFastFlowLoginUser();
      if (!loginUser) {
        this.SnackBar(true, "error", "Login user tidak ditemukan", 4000);
        return;
      }

      const currentId = this.normalizeFastFlowId(
        this.coverNoteRequest.fastflowId
      );
      if (currentId === "") {
        this.SnackBar(true, "error", "Fastflow ID wajib diisi", 3000);
        return;
      }

      const lookupPromise =
        this.coverNoteRequest.record &&
        this.normalizeFastFlowId(this.coverNoteRequest.record.fastflow_id) ===
          currentId
          ? Promise.resolve(this.coverNoteRequest.record)
          : this.LookupCoverNote(false);

      this.coverNoteRequest.submitting = true;

      lookupPromise
        .then(record => {
          if (!record || !record.fastflow_row) {
            throw new Error("Cover note tidak ditemukan");
          }

          this.coverNoteRequest.record = record;

          const formData = new FormData();
          formData.append("url_type", "print");
          formData.append("login_user", loginUser);
          formData.append("fastflow_row", record.fastflow_row);
          formData.append("fastflow_printflag", "EPOLIS");
          formData.append(
            "fastflow_mo",
            this.policySubmissionDialog.marketingOfficerId
          );
          const selectedMarketingOfficer = this.policySubmissionDialog.marketingOfficers.find(
            item =>
              item.users_id === this.policySubmissionDialog.marketingOfficerId
          );
          formData.append(
            "fastflow_branch",
            selectedMarketingOfficer
              ? selectedMarketingOfficer.users_branch
              : ""
          );
          formData.append("fastflow_appsource", "CALCULATOR_BANK");

          return this.$axios.post(
            this.$functions.SafeURL("apiWriteFastFlow", ""),
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data"
              }
            }
          );
        })
        .then(response => {
          const result =
            Array.isArray(response.data) && response.data.length > 0
              ? response.data[0]
              : null;

          if (!result || `${result.feedback || ""}`.trim() !== "Y") {
            throw new Error(
              (result && result.feedback) || "Gagal mengajukan policy"
            );
          }

          const fastflowId = (
            result.fastflow_id ||
            this.coverNoteRequest.fastflowId ||
            ""
          ).trim();
          const fastflowUrl = result.fastflow_url || "";
          this.closePolicySubmissionDialog();

          return this.downloadFastFlowDocument(fastflowUrl).then(
            downloadResult => {
              const isSuccess = downloadResult.success;
              this.SnackBar(
                true,
                isSuccess ? "success" : "warning",
                this.buildFastFlowResultMessage(
                  fastflowId,
                  "policy",
                  "policy",
                  downloadResult,
                  "Y"
                ),
                isSuccess ? 5000 : 9000
              );
            }
          );
        })
        .catch(error => {
          this.SnackBar(
            true,
            "error",
            error.message || "Gagal mengajukan policy dari cover note",
            4500
          );
        })
        .finally(() => {
          this.coverNoteRequest.submitting = false;
        });
    },
    ProcessFastFlowDocument(printFlag) {
      if (
        !this.ValidateDocumentAction() ||
        this.submittingDocumentType !== ""
      ) {
        return;
      }

      const isCoverNote = printFlag === "COVERNOTE";
      const successLabel = isCoverNote ? "cover note" : "policy";
      const failureLabel = isCoverNote ? "cover note" : "policy";
      const loginUser = this.getFastFlowLoginUser();

      this.submittingDocumentType = printFlag;
      const formData = new FormData();
      const today = this.$functions.Today();
      const selectedOccupancyLabel = this.selectedOccupancy
        ? this.selectedOccupancy.label
        : "";
      const fastFlowInsuredName = `${String(
        this.form.bankName || ""
      ).trim()} QQ ${String(this.form.participantName || "").trim()}`;

      formData.append("url_type", "add");
      formData.append("login_user", loginUser);
      formData.append("fastflow_printflag", printFlag);
      formData.append("fastflow_appsource", "CALCULATOR_BANK");
      formData.append("fastflow_row", "");
      formData.append("fastflow_type", "N");
      formData.append("fastflow_toc", "0101");
      formData.append("fastflow_signdate", today);
      formData.append("fastflow_signby", fastFlowInsuredName);
      formData.append("fastflow_sourcedate", today);
      formData.append("fastflow_sourceby", "");
      formData.append("fastflow_branchdate", today);
      formData.append("fastflow_branchby", loginUser);
      formData.append("fastflow_insured", fastFlowInsuredName);
      formData.append("fastflow_address", this.getStoredUserAddress());
      formData.append("fastflow_zipcode", this.form.zipCode);
      formData.append("fastflow_phone", this.form.phone);
      formData.append("fastflow_email", this.form.email);
      formData.append("fastflow_policyflag", "E");
      formData.append("fastflow_fieldid_1", "R01");
      formData.append("fastflow_fieldvalueid_1", this.selectedOccupancy.code);
      formData.append("fastflow_fieldvaluedesc_1", selectedOccupancyLabel);
      formData.append("fastflow_fieldid_2", "");
      formData.append("fastflow_fieldvalueid_2", "");
      formData.append("fastflow_fieldvaluedesc_2", this.form.bankName);
      formData.append("fastflow_fieldid_3", "R03");
      formData.append("fastflow_fieldvalueid_3", "");
      formData.append("fastflow_fieldvaluedesc_3", this.form.riskLocation);
      formData.append("fastflow_fieldid_4", "Z01");
      formData.append("fastflow_fieldvalueid_4", "");
      formData.append("fastflow_fieldvaluedesc_4", this.form.zipCode);
      formData.append("fastflow_fieldid_5", "R05");
      formData.append(
        "fastflow_fieldvalueid_5",
        this.selectedEscalation.id === ESCALATION_NONE_ID
          ? "ESC0"
          : this.selectedEscalation.id === ESCALATION_3_ID
          ? "ESC3"
          : "ESC5"
      );
      formData.append(
        "fastflow_fieldvaluedesc_5",
        this.selectedEscalationDisplay
      );
      formData.append(
        "fastflow_fieldvaluedesc_6",
        String(this.form.branchName || "").trim()
      );
      formData.append(
        "fastflow_tsi",
        this.normalizeNumber(this.form.buildingValue)
      );
      formData.append(
        "fastflow_add_tsi",
        this.normalizeNumber(this.form.furnitureValue)
      );
      formData.append(
        "fastflow_add_tsi_2",
        this.normalizeNumber(this.form.equipmentValue)
      );
      formData.append(
        "fastflow_add_tsi_3",
        this.normalizeNumber(this.form.stockValue)
      );
      formData.append("fastflow_fieldid_6", "");
      formData.append("fastflow_fieldvalueid_6", "");
      formData.append("fastflow_fieldvaluedesc_6", this.form.participantName);
      formData.append("fastflow_sdate", this.form.periodStartDate);
      formData.append("fastflow_edate", this.form.periodEndDate);
      formData.append("fastflow_period", this.policyRatePeriods || 1);
      formData.append("fastflow_main_type", "FLEXAS");
      formData.append(
        "fastflow_main_rate",
        this.formatApiNumber(this.ratePercent)
      );
      formData.append("fastflow_main_unit", "C");
      formData.append(
        "fastflow_main_cont",
        this.roundValue(this.baseContribution)
      );
      formData.append("fastflow_feeduty", this.policyFeeAmount);
      formData.append("fastflow_instype", "D");
      formData.append("fastflow_sourceujroh", 15);
      formData.append("fastflow_sourceujrohamt", this.bankUjrahAmount);
      formData.append("fastflow_upload", "");
      formData.append("fastflow_nonupload", "");
      formData.append("fastflow_periodunit", "Y");
      formData.append("fastflow_preview", "");
      formData.append("fastflow_mo", "");
      formData.append("fastflow_channel", "DIRECT");
      formData.append("fastflow_segment", "CORPORATE");
      formData.append("fastflow_sources", "");
      formData.append("fastflow_discount", 0);
      formData.append("fastflow_discount_rate", 0);

      this.$axios
        .post(this.$functions.SafeURL("apiWriteFastFlow", ``), formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then(response => {
          const result =
            Array.isArray(response.data) && response.data.length > 0
              ? response.data[0]
              : null;

          if (result) {
            const fastflowId = result.fastflow_id || "";
            const fastflowUrl = result.fastflow_url || "";
            const backendFeedback =
              typeof result.feedback === "string" ? result.feedback.trim() : "";

            return this.downloadFastFlowDocument(fastflowUrl).then(
              downloadResult => {
                const isSuccess =
                  backendFeedback === "Y" && downloadResult.success;

                this.SnackBar(
                  true,
                  isSuccess ? "success" : "warning",
                  this.buildFastFlowResultMessage(
                    fastflowId,
                    successLabel,
                    failureLabel,
                    downloadResult,
                    backendFeedback
                  ),
                  isSuccess ? 5000 : 9000
                );
              }
            );
          } else {
            this.SnackBar(
              true,
              "error",
              (result && result.feedback) || `Gagal proses ${failureLabel}`,
              4000
            );
          }
        })
        .catch(error => {
          const message =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            `Gagal proses ${failureLabel}`;

          this.SnackBar(true, "error", message, 4000);
        })
        .finally(() => {
          this.submittingDocumentType = "";
        });
    },
    formatCurrency(value) {
      const amount = this.normalizeNumber(value);

      return new Intl.NumberFormat("id-ID", {
        maximumFractionDigits: 2
      }).format(amount);
    },
    formatPercent(value) {
      const percent = this.normalizeNumber(value);

      return `${percent
        .toFixed(6)
        .replace(/0+$/, "")
        .replace(/\.$/, "")}%`;
    },
    formatApiNumber(value) {
      const amount = this.normalizeNumber(value);
      return amount.toFixed(6);
    },
    SnackBar(dialog, color, text, timeout) {
      this.snackbar.dialog = dialog;
      this.snackbar.color = color;
      this.snackbar.text = text;
      this.snackbar.timeout = timeout;
    }
  }
};
</script>

<style scoped>
.calculator-bank-shell {
  background: radial-gradient(
      circle at top right,
      rgba(17, 53, 104, 0.08),
      transparent 24%
    ),
    linear-gradient(180deg, #f4f8fb 0%, #edf4f3 100%);
  font-family: "Avenir Next", "Segoe UI", sans-serif;
}

.bank-page {
  border-radius: 22px !important;
  border: 1px solid rgba(16, 55, 98, 0.08) !important;
  background: linear-gradient(180deg, #ffffff, #f8fcfa);
  box-shadow: 0 18px 48px rgba(15, 46, 91, 0.08);
}

.compact-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  margin-bottom: 14px;
  border-radius: 18px;
  background: linear-gradient(135deg, #0b2d5c, #103d70 52%, #155852 100%);
  color: #f7f3ea;
}

.compact-title {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
}

.compact-subtitle {
  margin-top: 4px;
  color: rgba(247, 243, 234, 0.82);
  font-size: 13px;
}

.compact-badges {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.mini-badge {
  min-width: 92px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.mini-badge span {
  display: block;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(247, 243, 234, 0.7);
}

.mini-badge strong {
  display: block;
  margin-top: 4px;
  font-size: 15px;
  color: #fffdf8;
}

.mini-badge--status {
  background: rgba(255, 255, 255, 0.08);
}

.mini-badge--ready {
  background: rgba(12, 110, 104, 0.28);
}

.compact-layout {
  align-items: stretch;
  row-gap: 12px;
}

.bank-panel,
.summary-card {
  height: 100%;
  border-radius: 18px !important;
  border: 1px solid rgba(16, 55, 98, 0.08) !important;
  background: linear-gradient(180deg, #ffffff, #f9fcfb);
  box-shadow: 0 10px 28px rgba(15, 46, 91, 0.05);
}

.bank-panel--flat {
  min-height: 100%;
}

.panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 12px;
}

.panel-kicker {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
  color: #0b2d5c;
  margin-bottom: 4px;
}

.panel-title,
.summary-card__title {
  font-size: 18px;
  font-weight: 800;
  color: #16355c;
}

.panel-caption {
  max-width: 280px;
  text-align: right;
  color: #6c7f8f;
  font-size: 12px;
  line-height: 1.45;
}

.field-group-title {
  display: flex;
  align-items: center;
  margin-top: 2px;
  margin-bottom: 2px;
  padding-top: 6px;
  color: #155852;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.field-group-title::after {
  content: "";
  flex: 1;
  height: 1px;
  margin-left: 10px;
  background: linear-gradient(
    90deg,
    rgba(21, 88, 82, 0.22),
    rgba(11, 45, 92, 0.06)
  );
}

.field-group-caption {
  margin-top: -2px;
  margin-bottom: 6px;
  color: #6c7f8f;
  font-size: 12px;
  line-height: 1.45;
}

.field-row-spaced {
  margin-top: 8px;
}

.summary-note {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(16, 61, 112, 0.08);
  color: #103d70;
  font-size: 12px;
  line-height: 1.5;
}

.summary-note--error {
  background: rgba(21, 88, 82, 0.09);
  color: #155852;
}

.document-tabs {
  margin-top: 14px;
}

.document-tabs__nav {
  margin-bottom: 10px;
  border-radius: 14px;
  background: rgba(16, 61, 112, 0.05);
}

.document-tabs__nav--top {
  margin-bottom: 14px;
}

.document-tabs__items {
  background: transparent !important;
}

.document-tab-panel,
.policy-conversion-panel {
  padding: 14px;
  border-radius: 16px;
  background: linear-gradient(135deg, #f4f8fb, #eef7f4);
  border: 1px solid rgba(16, 61, 112, 0.1);
}

.document-tab-panel__intro {
  margin-bottom: 12px;
  color: #6c7f8f;
  font-size: 12px;
  line-height: 1.5;
}

.policy-conversion-panel__head {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.policy-conversion-panel__caption {
  color: #6c7f8f;
  font-size: 12px;
  line-height: 1.5;
}

.policy-conversion-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  height: 100%;
  align-items: stretch;
}

.policy-conversion-result {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.policy-conversion-result__item {
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(16, 61, 112, 0.08);
}

.policy-conversion-result__item span {
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6c7f8f;
}

.policy-conversion-result__item strong {
  display: block;
  color: #16355c;
  font-size: 13px;
  line-height: 1.4;
  word-break: break-word;
}

@media (max-width: 960px) {
  .policy-conversion-actions {
    grid-template-columns: 1fr;
  }
}

.gross-box {
  padding: 16px;
  border-radius: 16px;
  background: radial-gradient(
      circle at top right,
      rgba(74, 159, 151, 0.16),
      transparent 28%
    ),
    linear-gradient(135deg, #f3f8fd, #ebf5f3);
  border: 1px solid rgba(16, 61, 112, 0.12);
}

.gross-box__label {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #103d70;
  font-weight: 700;
}

.gross-box__value {
  margin-top: 6px;
  font-size: 28px;
  font-weight: 900;
  line-height: 1.05;
  color: #16355c;
}

.gross-box__meta {
  margin-top: 4px;
  font-size: 12px;
  color: #667b8f;
}

.compact-breakdown {
  margin-top: 12px;
  display: grid;
  gap: 8px;
}

.compact-breakdown__row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f4f8fb;
  color: #5a6f81;
  font-size: 13px;
}

.compact-breakdown__row strong {
  color: #173c59;
  text-align: right;
}

.compact-breakdown__row--net {
  background: rgba(21, 88, 82, 0.08);
}

.compact-info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}

.compact-info {
  padding: 10px 12px;
  border-radius: 12px;
  background: linear-gradient(180deg, #fbfdff, #f3f8fb);
  border: 1px solid rgba(16, 55, 98, 0.08);
}

.compact-info span {
  display: block;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #70857f;
}

.compact-info strong {
  display: block;
  margin-top: 4px;
  font-size: 13px;
  color: #173c59;
}

@media (max-width: 960px) {
  .compact-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .compact-badges {
    width: 100%;
    justify-content: flex-start;
  }

  .compact-breakdown__row {
    flex-wrap: wrap;
  }

  .compact-breakdown__row strong {
    width: 100%;
    text-align: left;
  }
}

.action-buttons {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.action-btn {
  min-height: 42px !important;
  border-radius: 12px !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  font-weight: 700 !important;
  box-shadow: 0 12px 22px rgba(15, 46, 91, 0.1);
}

.action-btn--primary {
  background: linear-gradient(120deg, #0b2d5c, #103d70) !important;
}

.action-btn--secondary {
  background: linear-gradient(120deg, #155852, #0c6e68) !important;
  color: #f6fbfc !important;
}

.bank-input ::v-deep .v-input__slot {
  border-radius: 12px !important;
  background: linear-gradient(
    180deg,
    rgba(248, 251, 253, 0.98),
    rgba(241, 247, 249, 0.98)
  );
  border-color: rgba(16, 55, 98, 0.16) !important;
}

.bank-input ::v-deep .v-label {
  color: #607588 !important;
}

.bank-input ::v-deep input,
.bank-input ::v-deep textarea {
  color: #173c59 !important;
}

.bank-input ::v-deep .v-input__append-inner .v-icon,
.bank-input ::v-deep .v-input__prepend-inner .v-icon {
  color: #103d70 !important;
}

@media (max-width: 600px) {
  .bank-page {
    padding: 12px !important;
    border-radius: 18px !important;
  }

  .compact-header {
    padding: 12px;
  }

  .panel-heading {
    flex-direction: column;
    gap: 6px;
  }

  .panel-caption {
    max-width: none;
    text-align: left;
  }

  .compact-title {
    font-size: 22px;
  }

  .field-row-spaced {
    margin-top: 4px;
  }

  .compact-badges,
  .compact-info-grid {
    width: 100%;
    grid-template-columns: 1fr;
  }
}
</style>
